import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MenuItemCard from './MenuItemCard';
import { Search } from 'lucide-react';

const ease = [0.16, 1, 0.3, 1];

// Dark-theme palette matching MenuItemCard
const CARD_BG = '#221A12';
const SKELETON_BG = 'rgba(255,236,206,.06)';
const HAIRLINE = 'rgba(255,236,206,.06)';
const HEADING = '#F5EDE0';
const MUTED = '#C8BFB1';

const SkeletonCard = ({ i }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.18), ease }}
    className="animate-pulse"
    style={{ borderRadius: '1.5rem', background: CARD_BG, border: `1px solid ${HAIRLINE}` }}
  >
    <div className="p-5 space-y-3">
      <div className="h-4 rounded-full w-2/3" style={{ background: SKELETON_BG }} />
      <div className="h-3 rounded-full w-full" style={{ background: SKELETON_BG }} />
      <div className="h-3 rounded-full w-5/6" style={{ background: SKELETON_BG }} />
      <div className="h-9 rounded-full w-1/3 mt-4" style={{ background: SKELETON_BG }} />
    </div>
  </motion.div>
);

const MenuGrid = ({ items, loading = false, onAddToCart, isOrderingOpen = true }) => {
  // Empty state (only when not loading and no items)
  if (!loading && items.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease }}
        className="text-center py-24"
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{ background: 'rgba(255,236,206,.05)', border: `1px solid ${HAIRLINE}` }}
        >
          <Search size={24} style={{ color: MUTED }} />
        </div>
        <h3
          className="text-xl font-semibold tracking-tight mb-2"
          style={{ color: HEADING, fontFamily: 'Outfit, sans-serif' }}
        >
          No items found
        </h3>
        <p className="text-sm max-w-sm mx-auto" style={{ color: MUTED }}>
          Try selecting a different category or location.
        </p>
      </motion.div>
    );
  }

  return (
    <>
      {/* Count — appears with the grid, not the skeleton */}
      <div className="flex items-center justify-between mb-8 h-5">
        <AnimatePresence mode="wait">
          {!loading && (
            <motion.p
              key="count"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease }}
              className="text-sm"
              style={{ color: MUTED }}
            >
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Crossfade: skeleton ↔ real cards */}
      <div className="relative">
        <AnimatePresence mode="wait" initial={false}>
          {loading ? (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease }}
              className="flex flex-col gap-3"
            >
              {[...Array(6)].map((_, i) => (
                <SkeletonCard key={i} i={i} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease }}
              className="flex flex-col gap-3"
            >
              {items.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: Math.min(i * 0.03, 0.45),
                    ease,
                  }}
                >
                  <MenuItemCard
                    item={item}
                    onAddToCart={onAddToCart}
                    isOrderingOpen={isOrderingOpen}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default MenuGrid;
